import {
  ProjectApiKeySection,
  ProjectCsvMappingSection,
  ProjectDataWipeSection,
  ProjectDetailsSection,
  ProjectMetricThresholdSection,
  ProjectSettingsAccordion,
  ProjectUploadTokensSection,
} from '@/features/projects/components';
import { Box, Stack, Text } from '@/ui';
import {
  settingsPanelsGap,
  settingsPageTypographySx,
} from '@/ui/composites/settingsPageTypographySx';
import { Section } from '@/ui/layout/Section';
import { PageShell } from '@/ui/primitives/PageShell';
import type { ProjectSettingsViewModel } from '@/features/projects/hooks/useProjectSettingsViewModel';
import type { ReactElement } from 'react';

type ProjectSettingsContentProps = {
  viewModel: ProjectSettingsViewModel;
  hideUploadTokenSettings: boolean;
  hidePipelineWebhookSettings: boolean;
  hidePipelineMetricThresholds: boolean;
};

export const ProjectSettingsContent = ({
  viewModel,
  hideUploadTokenSettings,
  hidePipelineWebhookSettings,
  hidePipelineMetricThresholds,
}: ProjectSettingsContentProps): ReactElement => {
  const resolvedProject = viewModel.project ?? null;

  return (
    <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="lg">
      <Stack gap="xl" sx={settingsPageTypographySx}>
        <Section>
          <Stack gap="xl">
            <Text role="pageTitle" component="h1">
              Project Settings
            </Text>
            <Stack gap={settingsPanelsGap}>
              <ProjectDetailsSection
                project={resolvedProject}
                nameValue={viewModel.nameValue}
                setNameValue={viewModel.setNameValue}
                nameDirty={viewModel.nameDirty}
                setNameDirty={viewModel.setNameDirty}
                badgeEnabledValue={viewModel.badgeEnabledValue}
                setBadgeEnabledValue={viewModel.setBadgeEnabledValue}
                badgeCharsValue={viewModel.badgeCharsValue}
                setBadgeCharsValue={viewModel.setBadgeCharsValue}
                badgeColorValue={viewModel.badgeColorValue}
                setBadgeColorValue={viewModel.setBadgeColorValue}
                updateProject={viewModel.updateProject}
                mutationError={viewModel.mutationError}
                notify={viewModel.notify}
              />
              {hidePipelineWebhookSettings ? null : (
                <ProjectSettingsAccordion
                  title="Pipeline Webhooks"
                  description="Generate a webhook key, add it to your CI provider, and test the connection."
                >
                  <ProjectApiKeySection
                    project={resolvedProject}
                    apiKeyValue={viewModel.apiKeyValue}
                    setApiKeyValue={viewModel.setApiKeyValue}
                    revokeOpen={viewModel.revokeOpen}
                    setRevokeOpen={viewModel.setRevokeOpen}
                    updateProject={viewModel.updateProject}
                    rotateProjectApiKey={viewModel.rotateProjectApiKey}
                    testProjectPipelineWebhook={viewModel.testProjectPipelineWebhook}
                    apiKeyError={viewModel.apiKeyError}
                    notify={viewModel.notify}
                    container="none"
                  />
                </ProjectSettingsAccordion>
              )}
              {resolvedProject && !hideUploadTokenSettings ? (
                <ProjectSettingsAccordion
                  title="API Upload Token"
                  description={`Generate an API token to upload results files to ${resolvedProject.name}.`}
                >
                  <ProjectUploadTokensSection
                    projectId={resolvedProject.id}
                    projectName={resolvedProject.name}
                    container="none"
                  />
                </ProjectSettingsAccordion>
              ) : null}
              {resolvedProject ? (
                <Box id="xlsx-csv-mapping">
                  <ProjectCsvMappingSection
                    projectId={resolvedProject.id}
                    notify={viewModel.notify}
                  />
                </Box>
              ) : null}
              <ProjectSettingsAccordion
                title="Execution Run Metric Thresholds"
                description="Set target and warning thresholds for execution run metrics."
              >
                <ProjectMetricThresholdSection
                  title="Execution Run Metric Thresholds"
                  description="Set target and warning thresholds for execution run metrics."
                  successMessage="Saved execution run metric thresholds"
                  thresholdKey="executionRuns"
                  drafts={viewModel.executionRunThresholdDrafts}
                  setDrafts={viewModel.setExecutionRunThresholdDrafts}
                  preferredUnitsRef={viewModel.executionRunPreferredUnitsRef}
                  isDirty={viewModel.executionRunThresholdsDirty}
                  isValid={viewModel.executionRunThresholdsValid}
                  isPending={viewModel.updateProject.isPending}
                  gridSpacing="sm"
                  project={resolvedProject}
                  updateProject={viewModel.updateProject}
                  mutationError={viewModel.mutationError}
                  notify={viewModel.notify}
                  resetDrafts={viewModel.resetExecutionRunThresholdDrafts}
                  container="none"
                />
              </ProjectSettingsAccordion>
              <ProjectSettingsAccordion
                title="Suite Metric Thresholds"
                description="Set target and warning thresholds for suite-level quality metrics."
              >
                <ProjectMetricThresholdSection
                  title="Suite Metric Thresholds"
                  description="Set target and warning thresholds for suite-level quality metrics."
                  successMessage="Saved suite metric thresholds"
                  thresholdKey="suites"
                  drafts={viewModel.suiteThresholdDrafts}
                  setDrafts={viewModel.setSuiteThresholdDrafts}
                  preferredUnitsRef={viewModel.suitePreferredUnitsRef}
                  isDirty={viewModel.suiteThresholdsDirty}
                  isValid={viewModel.suiteThresholdsValid}
                  isPending={viewModel.updateProject.isPending}
                  gridSpacing="sm"
                  project={resolvedProject}
                  updateProject={viewModel.updateProject}
                  mutationError={viewModel.mutationError}
                  notify={viewModel.notify}
                  resetDrafts={viewModel.resetSuiteThresholdDrafts}
                  container="none"
                />
              </ProjectSettingsAccordion>
              <ProjectSettingsAccordion
                title="Test Metric Thresholds"
                description="Set target and warning thresholds for test-level quality metrics."
              >
                <ProjectMetricThresholdSection
                  title="Test Metric Thresholds"
                  description="Set target and warning thresholds for test-level quality metrics."
                  successMessage="Saved test metric thresholds"
                  thresholdKey="tests"
                  drafts={viewModel.testThresholdDrafts}
                  setDrafts={viewModel.setTestThresholdDrafts}
                  preferredUnitsRef={viewModel.testPreferredUnitsRef}
                  isDirty={viewModel.testThresholdsDirty}
                  isValid={viewModel.testThresholdsValid}
                  isPending={viewModel.updateProject.isPending}
                  gridSpacing="sm"
                  project={resolvedProject}
                  updateProject={viewModel.updateProject}
                  mutationError={viewModel.mutationError}
                  notify={viewModel.notify}
                  resetDrafts={viewModel.resetTestThresholdDrafts}
                  container="none"
                />
              </ProjectSettingsAccordion>
              {hidePipelineMetricThresholds ? null : (
                <ProjectSettingsAccordion
                  title="Pipeline Metric Thresholds"
                  description="Set target and warning thresholds for pipeline-level quality metrics."
                >
                  <ProjectMetricThresholdSection
                    title="Pipeline Metric Thresholds"
                    description="Set target and warning thresholds for pipeline-level quality metrics."
                    successMessage="Saved pipeline metric thresholds"
                    thresholdKey="pipelines"
                    drafts={viewModel.pipelineThresholdDrafts}
                    setDrafts={viewModel.setPipelineThresholdDrafts}
                    preferredUnitsRef={viewModel.pipelinePreferredUnitsRef}
                    isDirty={viewModel.pipelineThresholdsDirty}
                    isValid={viewModel.pipelineThresholdsValid}
                    isPending={viewModel.updateProject.isPending}
                    gridSpacing="sm"
                    project={resolvedProject}
                    updateProject={viewModel.updateProject}
                    mutationError={viewModel.mutationError}
                    notify={viewModel.notify}
                    resetDrafts={viewModel.resetPipelineThresholdDrafts}
                    container="none"
                  />
                </ProjectSettingsAccordion>
              )}
              {hidePipelineMetricThresholds ? null : (
                <ProjectSettingsAccordion
                  title="Stage Metric Thresholds"
                  description="Set target and warning thresholds for stage-level quality metrics."
                >
                  <ProjectMetricThresholdSection
                    title="Stage Metric Thresholds"
                    description="Set target and warning thresholds for stage-level quality metrics."
                    successMessage="Saved stage metric thresholds"
                    thresholdKey="stages"
                    drafts={viewModel.stageThresholdDrafts}
                    setDrafts={viewModel.setStageThresholdDrafts}
                    preferredUnitsRef={viewModel.stagePreferredUnitsRef}
                    isDirty={viewModel.stageThresholdsDirty}
                    isValid={viewModel.stageThresholdsValid}
                    isPending={viewModel.updateProject.isPending}
                    gridSpacing="sm"
                    project={resolvedProject}
                    updateProject={viewModel.updateProject}
                    mutationError={viewModel.mutationError}
                    notify={viewModel.notify}
                    resetDrafts={viewModel.resetStageThresholdDrafts}
                    container="none"
                  />
                </ProjectSettingsAccordion>
              )}
              {hidePipelineMetricThresholds ? null : (
                <ProjectSettingsAccordion
                  title="Job Metric Thresholds"
                  description="Set target and warning thresholds for job-level quality metrics."
                >
                  <ProjectMetricThresholdSection
                    title="Job Metric Thresholds"
                    description="Set target and warning thresholds for job-level quality metrics."
                    successMessage="Saved job metric thresholds"
                    thresholdKey="jobs"
                    drafts={viewModel.jobThresholdDrafts}
                    setDrafts={viewModel.setJobThresholdDrafts}
                    preferredUnitsRef={viewModel.jobPreferredUnitsRef}
                    isDirty={viewModel.jobThresholdsDirty}
                    isValid={viewModel.jobThresholdsValid}
                    isPending={viewModel.updateProject.isPending}
                    gridSpacing="sm"
                    project={resolvedProject}
                    updateProject={viewModel.updateProject}
                    mutationError={viewModel.mutationError}
                    notify={viewModel.notify}
                    resetDrafts={viewModel.resetJobThresholdDrafts}
                    container="none"
                  />
                </ProjectSettingsAccordion>
              )}
              <ProjectDataWipeSection
                project={resolvedProject}
                deleteProject={viewModel.deleteProject}
                wipeProjectData={viewModel.wipeProjectData}
                mutationError={viewModel.mutationError}
                notify={viewModel.notify}
                onProjectDeleted={() => {
                  void viewModel.navigate('/projects', {
                    replace: true,
                    state: { notice: 'Project deleted' },
                  });
                }}
              />
            </Stack>
          </Stack>
        </Section>
      </Stack>
    </PageShell>
  );
};
